import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";

/**
 * Texas Hold'em UI — server-authoritative.
 * Listens to ws messages: poker_state (public table), poker_private (hole cards), poker_error.
 * Sends: poker_sit, poker_leave, poker_start, poker_action.
 */
export default function Holdem({ ws, isStreamer, channelUsername }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [hole, setHole] = useState([]);
  const [err, setErr] = useState("");
  const [raiseAmt, setRaiseAmt] = useState(20);
  const [seated, setSeated] = useState(false);
  const [myPeerId, setMyPeerId] = useState(null);

  useEffect(() => {
    if (!ws) return;
    const onMsg = (e) => {
      const m = JSON.parse(e.data);
      if (m.type === "welcome") setMyPeerId(m.peer_id);
      if (m.type === "poker_state") setState(m.state);
      if (m.type === "poker_private") setHole(m.hole || []);
      if (m.type === "poker_error") { setErr(m.error); setTimeout(() => setErr(""), 3000); }
    };
    ws.addEventListener("message", onMsg);
    return () => ws.removeEventListener("message", onMsg);
  }, [ws]);

  const send = (type, extra = {}) => {
    if (!ws || ws.readyState !== 1) return;
    ws.send(JSON.stringify({ type, ...extra }));
  };

  const sit = () => { send("poker_sit"); setSeated(true); };
  const leave = () => { send("poker_leave"); setSeated(false); setHole([]); };
  const start = () => send("poker_start");
  const act = (action, amount = 0) => send("poker_action", { action, amount });

  const me = state?.players?.find(p => p.peer_id === myPeerId);
  const isMyTurn = state?.current_peer === myPeerId;

  const Card = ({ c }) => {
    if (!c) return <div className="w-12 h-16 rounded-sm border border-dashed border-white/10 bg-black/30" />;
    const red = c.includes("♥") || c.includes("♦");
    return (
      <div className="w-12 h-16 rounded-sm bg-white text-black flex flex-col items-center justify-center font-bold border border-white/20" style={{ color: red ? "#dc2626" : "#000" }}>
        <div className="text-lg leading-none">{c[0]}</div>
        <div className="text-xl leading-none mt-1">{c[1] || ""}</div>
      </div>
    );
  };

  if (!user) {
    return <div className="bg-black/60 border border-white/10 rounded-md p-6 text-center text-zinc-300">Log in to play Texas Hold'em.</div>;
  }

  return (
    <div data-testid="game-holdem" className="bg-black/60 border border-white/10 rounded-md p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="sc-heading text-xl font-bold">Texas Hold'em</div>
          <div className="text-xs text-zinc-400">Stage: <span className="text-[#00E5FF] uppercase">{state?.stage || "waiting"}</span> · Pot: <span className="text-[#F59E0B] font-bold">{state?.pot || 0}</span></div>
        </div>
        <div className="flex gap-2">
          {!seated && <button data-testid="holdem-sit" onClick={sit} className="sc-btn-ghost text-xs">Sit at Table</button>}
          {seated && <button data-testid="holdem-leave" onClick={leave} className="sc-btn-ghost text-xs">Leave Seat</button>}
          {isStreamer && state?.stage !== "preflop" && state?.stage !== "flop" && state?.stage !== "turn" && state?.stage !== "river" && (
            <button data-testid="holdem-start" onClick={start} className="sc-btn text-xs">Deal New Hand</button>
          )}
        </div>
      </div>

      {/* Board */}
      <div className="bg-gradient-to-b from-[#0c2e14] to-[#062a0e] border border-[#10B981]/40 rounded-md p-6 mb-4">
        <div className="text-[10px] sc-label text-center mb-3" style={{ color: "#10B981" }}>COMMUNITY CARDS</div>
        <div className="flex gap-2 justify-center mb-5">
          {[0,1,2,3,4].map(i => <Card key={i} c={(state?.board || [])[i]} />)}
        </div>
        {state?.last_action && <div className="text-xs text-zinc-300 text-center italic">{state.last_action}</div>}
        {state?.winner && <div className="text-center mt-2 text-[#F59E0B] font-bold">🏆 {state.winner} wins{state.winners_info?.[0]?.hand ? ` — ${state.winners_info[0].hand}` : ""}</div>}
      </div>

      {/* Players */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
        {(state?.players || []).map(p => (
          <div key={p.peer_id} className={`p-3 border rounded-sm ${p.peer_id === state?.current_peer ? "border-[#FF3366] bg-[#FF3366]/10" : p.folded ? "border-white/5 opacity-40" : "border-white/10"}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">
                  {p.name}
                  {p.peer_id === state?.dealer_peer && <span className="ml-1 text-[10px] tracking-widest uppercase text-[#F59E0B]">D</span>}
                </div>
                <div className="text-xs text-zinc-400">Chips: {p.chips} · Bet: {p.bet}</div>
              </div>
              {p.folded && <span className="text-[10px] tracking-widest uppercase text-zinc-500">Fold</span>}
            </div>
            {/* Show hole cards (showdown or own) */}
            {(p.hole || (p.peer_id === myPeerId && hole.length)) && (
              <div className="flex gap-1 mt-2">
                {(p.hole || hole).map((c, i) => <Card key={i} c={c} />)}
              </div>
            )}
          </div>
        ))}
        {(!state?.players || state.players.length === 0) && (
          <div className="col-span-full text-center text-xs text-zinc-500 py-4">No players seated yet. Click "Sit at Table" to join.</div>
        )}
      </div>

      {/* Actions */}
      {seated && isMyTurn && (
        <div className="border border-[#FF3366]/40 bg-[#FF3366]/5 rounded-sm p-3">
          <div className="text-xs sc-label text-[#FF3366] mb-2">Your turn — Call: {state?.current_bet - (me?.bet || 0)}</div>
          <div className="flex flex-wrap gap-2">
            <button data-testid="holdem-fold" onClick={() => act("fold")} className="sc-btn-ghost text-xs">Fold</button>
            {state?.current_bet === me?.bet ? (
              <button data-testid="holdem-check" onClick={() => act("check")} className="sc-btn-ghost text-xs">Check</button>
            ) : (
              <button data-testid="holdem-call" onClick={() => act("call")} className="sc-btn text-xs">Call {state?.current_bet - (me?.bet || 0)}</button>
            )}
            <input type="number" min={20} value={raiseAmt} onChange={e => setRaiseAmt(Number(e.target.value))} className="sc-input max-w-[100px] text-xs" />
            <button data-testid="holdem-raise" onClick={() => act("raise", raiseAmt)} className="sc-btn text-xs">Raise {raiseAmt}</button>
          </div>
        </div>
      )}
      {seated && !isMyTurn && state?.stage !== "waiting" && state?.stage !== "showdown" && (
        <div className="text-xs text-zinc-400 text-center">Waiting for <span className="text-white font-bold">{state?.players?.find(p => p.peer_id === state?.current_peer)?.name}</span>...</div>
      )}
      {err && <div className="text-[#FF3366] text-xs mt-2 text-center">{err}</div>}
      <div className="text-[10px] text-zinc-500 text-center mt-4">
        Free-to-play with chip stacks (no real money). Streamer deals new hands. Minimum 2 seated players to start.
      </div>
    </div>
  );
}
